import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Mail, Phone, MapPin, Calendar, Shield } from "lucide-react";
import type { UserInfo } from "@/types/superAdmin";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { useTranslation } from "react-i18next";

interface UserDetailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: UserInfo | null;
}

export function UserDetailsDialog({
  open,
  onOpenChange,
  user,
}: UserDetailsDialogProps) {
  const { t } = useTranslation(["superadmin", "common"]);
  if (!user) return null;

  const fullName = `${user.first_name} ${user.last_name}`.trim() || user.email;
  const initials = user.first_name && user.last_name
    ? `${user.first_name[0]}${user.last_name[0]}`.toUpperCase()
    : user.email[0].toUpperCase();

  const roleColors: Record<string, string> = {
    admin: "bg-purple-500",
    coach: "bg-blue-500",
    client: "bg-green-500",
    super_admin: "bg-red-500",
  };

  const roleLabels: Record<string, string> = {
    admin: t("userDetails.roles.admin"),
    coach: t("userDetails.roles.coach"),
    client: t("userDetails.roles.client"),
    super_admin: t("userDetails.roles.superAdmin"),
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("userDetails.title")}</DialogTitle>
          <DialogDescription>
            {t("userDetails.description")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Profile Section */}
          <div className="flex items-start gap-4">
            <Avatar className="h-16 w-16">
              <AvatarFallback className="text-lg">{initials}</AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <h3 className="text-xl font-semibold">{fullName}</h3>
              <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                <Mail className="h-4 w-4" />
                {user.email}
              </div>
              {user.phone && (
                <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                  <Phone className="h-4 w-4" />
                  {user.phone}
                </div>
              )}
            </div>
          </div>

          <Separator />

          {/* Roles Section */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Shield className="h-4 w-4 text-muted-foreground" />
              <h4 className="font-semibold">{t("userDetails.rolesAndAccess")}</h4>
            </div>
            <div className="space-y-2">
              {user.roles.map((role, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Badge className={roleColors[role.role] || "bg-gray-500"}>
                    {roleLabels[role.role] || role.role}
                  </Badge>
                  {role.gym_name && (
                    <div className="flex items-center gap-1 text-sm text-muted-foreground">
                      <MapPin className="h-3 w-3" />
                      <span>{role.gym_name}</span>
                      {role.city && <span className="text-xs">({role.city})</span>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <Separator />

          {/* Activity Section */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <h4 className="font-semibold">{t("userDetails.activity")}</h4>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t("userDetails.status")} :</span>
                <Badge variant={user.status === 'active' ? 'default' : 'secondary'}>
                  {user.status === 'active' ? t("userDetails.active") : t("userDetails.inactive")}
                </Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t("userDetails.lastSignIn")} :</span>
                <span>
                  {user.last_sign_in_at
                    ? format(new Date(user.last_sign_in_at), "Pp", { locale: fr })
                    : t("userDetails.neverSignedIn")}
                </span>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}